import generateChallenge from 'pkce-challenge'
import { config } from '../../config'

const createPkceData = async () => {
	const { code_challenge, code_verifier } = await generateChallenge()
	sessionStorage.setItem('pkce_code_verifier', code_verifier)
	return code_challenge
}

const generateState = () => {
	const state = crypto.randomUUID()
	sessionStorage.setItem('oauth_state', state)
	return state
}

export const getCodeVerifier = () => {
	return sessionStorage.getItem('pkce_code_verifier')
}

export const getState = () => {
	return sessionStorage.getItem('oauth_state')
}

export const clearAuthParams = () => {
	sessionStorage.removeItem('oauth_state')
	sessionStorage.removeItem('pkce_code_verifier')
	sessionStorage.removeItem('auth_email')
	sessionStorage.removeItem('sso_target_link_uri')
}

export const storeSsoTargetLinkUri = (uri: string) => {
	sessionStorage.setItem('sso_target_link_uri', uri)
}

export const getSsoTargetLinkUri = () => {
	return sessionStorage.getItem('sso_target_link_uri')
}

export const storeIdToken = (idToken: string) => {
	sessionStorage.setItem('okta_id_token', idToken)
}

export const getIdToken = () => {
	return sessionStorage.getItem('okta_id_token')
}

export const clearStorage = () => {
	clearAuthParams()
	sessionStorage.removeItem('okta_id_token')
	// Clear localStorage (where sessionStore persists user data)
	localStorage.removeItem('session-storage')
}

const beginLoginWithOkta = async (email: string, rememberMe: boolean): Promise<void> => {
	sessionStorage.setItem('auth_email', email)
	const state = generateState()
	const codeChallenge = await createPkceData()

	const authUrl = new URL(`${config.okta.issuer}/v1/authorize`)
	authUrl.searchParams.append('client_id', config.okta.clientId)
	authUrl.searchParams.append('response_type', 'code')
	authUrl.searchParams.append('scope', 'openid profile email offline_access')
	authUrl.searchParams.append('redirect_uri', `${window.location.origin}/auth/callback`)
	authUrl.searchParams.append('code_challenge', codeChallenge)
	authUrl.searchParams.append('code_challenge_method', 'S256')
	authUrl.searchParams.append('state', state)
	authUrl.searchParams.append('login_hint', email) // Pre-fill email in Okta
	authUrl.searchParams.append('remember_me', rememberMe ? 'true' : 'false')
	window.location.assign(authUrl.toString())
}

/**
 * Build the Okta logout URL to end the SSO session
 * This ensures users can switch accounts after logout
 */
export const getOktaLogoutUrl = (): string => {
	const logoutUrl = new URL(`${config.okta.issuer}/v1/logout`)
	logoutUrl.searchParams.append('client_id', config.okta.clientId)
	logoutUrl.searchParams.append('post_logout_redirect_uri', `${window.location.origin}/auth/login`)
	const idToken = getIdToken()
	if (idToken) {
		logoutUrl.searchParams.append('id_token_hint', idToken)
	}
	return logoutUrl.toString()
}

const beginLoginLocally = async (email: string, _rememberMe: boolean) => {
	sessionStorage.setItem('auth_email', email)
	const state = generateState()
	const codeChallenge = await createPkceData()
	window.location.href = `${window.location.origin}/auth/callback?code=LOCAL_DEV_CODE:${email}&state=${state}&code_verifier=${codeChallenge}`
}

/**
 * Begin the login process after email is entered
 * @param {string} email
 */
export const beginLogin = async (email: string, rememberMe: boolean) => {
	// Clear any SSO target URI so a standard login never accidentally inherits it
	sessionStorage.removeItem('sso_target_link_uri')
	if (config.env === 'local' || config.env === 'dev' || config.env === 'demo') {
		return beginLoginLocally(email, rememberMe)
	}
	return beginLoginWithOkta(email, rememberMe)
}

interface SsoLaunchOptions {
	idp?: string
	loginHint?: string
	targetLinkUri?: string
}

const parseSafeTargetLinkUri = (targetLinkUri: string): string | null => {
	try {
		// Accept root-relative paths only (not protocol-relative // paths).
		// Normalise through URL to strip control characters and other oddities.
		if (targetLinkUri.startsWith('/') && !targetLinkUri.startsWith('//')) {
			const parsed = new URL(targetLinkUri, window.location.origin)
			return parsed.pathname + parsed.search + parsed.hash
		}
		const parsed = new URL(targetLinkUri)
		if (parsed.origin === window.location.origin) {
			return parsed.pathname + parsed.search + parsed.hash
		}
		return null
	} catch {
		return null
	}
}

/**
 * Begin an IdP-initiated SSO login (Third-Party Initiated Login, OIDC Core §4).
 * Used when the user arrives from a shortcut in their IdP dashboard (e.g. Microsoft MyApps).
 * Starts the PKCE flow and redirects to Okta, optionally specifying which federated IdP to
 * use so that Okta skips its own login UI.
 */
export const beginSsoLaunch = async ({ idp, loginHint, targetLinkUri }: SsoLaunchOptions = {}): Promise<void> => {
	if (config.env === 'local' || config.env === 'dev' || config.env === 'demo') {
		if (targetLinkUri) {
			const safe = parseSafeTargetLinkUri(targetLinkUri)
			if (safe) storeSsoTargetLinkUri(safe)
		}
		if (loginHint) {
			return beginLoginLocally(loginHint, false)
		}
		window.location.assign(`${window.location.origin}/auth/login`)
		return
	}

	const state = generateState()
	const codeChallenge = await createPkceData()

	// Only store target_link_uri after PKCE succeeds to avoid polluting
	// sessionStorage if createPkceData() throws
	if (targetLinkUri) {
		const safe = parseSafeTargetLinkUri(targetLinkUri)
		if (safe) storeSsoTargetLinkUri(safe)
	}

	const authUrl = new URL(`${config.okta.issuer}/v1/authorize`)
	authUrl.searchParams.append('client_id', config.okta.clientId)
	authUrl.searchParams.append('response_type', 'code')
	authUrl.searchParams.append('scope', 'openid profile email offline_access')
	authUrl.searchParams.append('redirect_uri', `${window.location.origin}/auth/callback`)
	authUrl.searchParams.append('code_challenge', codeChallenge)
	authUrl.searchParams.append('code_challenge_method', 'S256')
	authUrl.searchParams.append('state', state)
	if (idp) {
		authUrl.searchParams.append('idp', idp)
	}
	if (loginHint) {
		authUrl.searchParams.append('login_hint', loginHint)
	}
	window.location.assign(authUrl.toString())
}
